import { Button } from "antd"
import classNames from "classnames"
import React, { FC, useMemo, useState } from "react"
import css from "./DrillInView.module.css"

export type DrillOption = { value: string; label: string }

// Vollbild-Drill-in-Unteransicht eines einzelnen Filterfeldes.
// - mode="single": suchbare Optionsliste (Einzelauswahl); Tipp setzt sofort + zurück.
//   Eine "Alle …"-Clear-Zeile erscheint nur ohne aktive Suche.
// - mode="multi": Checkliste mit lokaler TEMP-Auswahl; erst "Übernehmen" bestätigt,
//   "Zurück" verwirft die TEMP-Auswahl.
// Auf Modul-Ebene definiert (kein Remount bei jedem Eltern-Render); rendert im
// Dokumentfluss, kein position:fixed/absolute-Overlay (iFrame-robust).
export type DrillInViewProps = {
    mode: "single" | "multi"
    title: string
    options: DrillOption[]
    selected: string | string[] | null
    searchable: boolean
    searchPlaceholder: string
    searchNoResultsLabel: string
    backLabel: string
    clearAllLabel?: string
    applyLabel?: string
    onPickSingle: (value: string | null) => void
    onApplyMulti: (values: string[]) => void
    onBack: () => void
}

const DrillInView: FC<DrillInViewProps> = ({
    mode,
    title,
    options,
    selected,
    searchable,
    searchPlaceholder,
    searchNoResultsLabel,
    backLabel,
    clearAllLabel,
    applyLabel,
    onPickSingle,
    onApplyMulti,
    onBack,
}) => {
    const isMulti = mode === "multi"

    const [query, setQuery] = useState("")
    // TEMP-Auswahl für Multi: lokaler Entwurf, erst bei "Übernehmen" übernommen.
    const [temp, setTemp] = useState<string[]>(Array.isArray(selected) ? selected : [])

    const singleSelected = !isMulti && typeof selected === "string" ? selected : null

    const normalizedQuery = query.trim().toLowerCase()
    const filteredOptions = useMemo(() => {
        if (!normalizedQuery) return options
        return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery))
    }, [options, normalizedQuery])

    const toggleTemp = (value: string) => {
        setTemp((prev) => (prev.includes(value) ? prev.filter((entry) => entry !== value) : [...prev, value]))
    }

    // Clear-Zeile nur im Single-Modus und nur ohne aktive Suche.
    const showClearRow = !isMulti && !!clearAllLabel && normalizedQuery === ""

    const handleKeyActivate = (event: React.KeyboardEvent, action: () => void) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            action()
        }
    }

    return (
        <div className={css.drill} data-testid={`drill-${mode}`}>
            <div className={css.head}>
                <button type='button' className={css.back} aria-label={backLabel} onClick={onBack} data-testid='drill-back'>
                    ‹
                </button>
                <span className={css.title}>{title}</span>
            </div>

            {searchable && (
                <div className={css.searchWrap}>
                    <input
                        type='text'
                        className={css.searchInput}
                        data-testid='drill-search-input'
                        placeholder={searchPlaceholder}
                        value={query}
                        autoComplete='off'
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </div>
            )}

            <div className={css.list}>
                {showClearRow && (
                    <div
                        className={classNames(css.optionRow, css.clearAllRow, singleSelected === null && css.optionRowActive)}
                        role='button'
                        tabIndex={0}
                        data-testid='clear-all-row'
                        onClick={() => onPickSingle(null)}
                        onKeyDown={(event) => handleKeyActivate(event, () => onPickSingle(null))}
                    >
                        <span className={css.optionLabel}>{clearAllLabel}</span>
                    </div>
                )}

                {filteredOptions.length === 0 ? (
                    <div className={css.noResult}>{searchNoResultsLabel}</div>
                ) : (
                    filteredOptions.map((option) => {
                        const isSelected = isMulti ? temp.includes(option.value) : singleSelected === option.value
                        const activate = () => (isMulti ? toggleTemp(option.value) : onPickSingle(option.value))
                        return (
                            <div
                                key={option.value}
                                className={classNames(css.optionRow, isSelected && css.optionRowActive)}
                                role={isMulti ? "checkbox" : "button"}
                                aria-checked={isMulti ? isSelected : undefined}
                                tabIndex={0}
                                data-testid={`drill-option-${option.value}`}
                                onClick={activate}
                                onKeyDown={(event) => handleKeyActivate(event, activate)}
                            >
                                {isMulti && <span className={css.box}>{isSelected ? "✓" : ""}</span>}
                                <span className={css.optionLabel}>{option.label}</span>
                                {!isMulti && isSelected && <span className={css.tick}>✓</span>}
                            </div>
                        )
                    })
                )}
            </div>

            {isMulti && (
                <div className={css.applyBar}>
                    <Button type='primary' block data-testid='apply-selection-button' onClick={() => onApplyMulti(temp)}>
                        {applyLabel}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default DrillInView
